/* eslint-disable react/require-default-props */
import React, { useState, useCallback, useMemo, useId, useContext, useRef, useEffect, createContext } from 'react';
import PropTypes from 'prop-types';
import Typography from '../Typography';
import Button from '../Button';
import { Input } from '../Input';
import { FlexRow, FlexColumn } from '../GridsAndLayouts/GridsAndLayouts';
import { ChevronDownIcon, SearchIcon } from '../Icons';
import './Select.scss';

const SelectCloseContext = createContext(() => {});

const SelectOptionRow = ({ option, selected, onPick }) => {
  const closeSelect = useContext(SelectCloseContext);

  const handleClick = () => {
    onPick(option.value);
    closeSelect();
  };

  return (
    <Button
      variant="tertiary"
      size="sm"
      type="button"
      role="menuitem"
      fullWidth
      className={['ds-field-select__option', selected ? 'ds-field-select__option--selected' : '']
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
    >
      {option.avatar != null && (
        <FlexRow className="ds-field-select__avatar" align="center" justify="center" inline fullWidth={false}>
          {option.avatar}
        </FlexRow>
      )}
      <FlexColumn className="ds-field-select__option-text" align="start" fullWidth>
        <Typography variant="t15" component="span" className="ds-field-select__option-title">
          {option.label}
        </Typography>
        {option.subtitle != null && option.subtitle !== '' && (
          <Typography variant="t20" component="span" className="ds-field-select__option-subtitle">
            {option.subtitle}
          </Typography>
        )}
      </FlexColumn>
    </Button>
  );
};

SelectOptionRow.propTypes = {
  option: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.node.isRequired,
    subtitle: PropTypes.node,
    avatar: PropTypes.node,
  }).isRequired,
  selected: PropTypes.bool.isRequired,
  onPick: PropTypes.func.isRequired,
};

const mergeRootStyle = (base, vars) => {
  const out = { ...base };
  Object.entries(vars).forEach(([k, v]) => {
    if (v != null && v !== '') out[k] = v;
  });
  return out;
};

/**
 * Standalone single-select combobox (same visual language as the design-system dropdown select).
 * Use `variant="affix"` inside `InputGroup` prefix/suffix; default `field` for full-width fields.
 */
const Select = ({
  options,
  value,
  defaultValue,
  onChange,
  placeholder = 'Select',
  searchPlaceholder = 'Search…',
  searchable = false,
  disabled = false,
  align = 'left',
  className = '',
  style,
  variant = 'field',
  size,
  block = false,
  showChevron = true,
  searchIcon,
  id,
  menuClassName = '',
  renderSelectedLabel,
  triggerMinHeight,
  triggerWidth,
  menuMinWidth,
  menuMaxWidth,
  listMaxHeight,
  ariaLabel,
  error = false,
}) => {
  const generatedId = useId();
  const controlId = id || generatedId;
  const searchInputId = `${controlId}-search`;
  const rootRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const selectedValue = isControlled ? value : internalValue;
  const [query, setQuery] = useState('');

  const closeSelect = useCallback(() => {
    setIsOpen(false);
    setQuery('');
  }, []);

  const toggleOpen = () => {
    if (disabled) return;
    setIsOpen((o) => !o);
  };

  const handleTriggerKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleOpen();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        closeSelect();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeSelect]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') closeSelect();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeSelect]);

  useEffect(() => {
    if (disabled && isOpen) closeSelect();
  }, [disabled, isOpen, closeSelect]);

  const setValue = useCallback(
    (next) => {
      if (!isControlled) {
        setInternalValue(next);
      }
      if (onChange) {
        onChange(next);
      }
    },
    [isControlled, onChange]
  );

  const filteredOptions = useMemo(() => {
    if (!searchable || !query.trim()) {
      return options;
    }
    const q = query.trim().toLowerCase();
    return options.filter((opt) => {
      const labelStr = typeof opt.label === 'string' ? opt.label : '';
      const subStr = typeof opt.subtitle === 'string' ? opt.subtitle : '';
      return `${labelStr} ${subStr}`.toLowerCase().includes(q);
    });
  }, [options, query, searchable]);

  const selectedOption = useMemo(
    () => options.find((o) => String(o.value) === String(selectedValue)),
    [options, selectedValue]
  );

  const rootVars = mergeRootStyle(style || {}, {
    ...(triggerMinHeight != null ? { '--ds-field-select-trigger-min-height': triggerMinHeight } : {}),
    ...(triggerWidth != null ? { '--ds-field-select-trigger-width': triggerWidth } : {}),
    ...(menuMinWidth != null ? { '--ds-field-select-menu-min-width': menuMinWidth } : {}),
    ...(menuMaxWidth != null ? { '--ds-field-select-menu-max-width': menuMaxWidth } : {}),
    ...(listMaxHeight != null ? { '--ds-field-select-list-max-height': listMaxHeight } : {}),
  });

  const showSearchIcon = searchable && searchIcon !== false;

  const searchIconNode = useMemo(() => {
    if (!showSearchIcon) return null;
    if (searchIcon !== undefined && searchIcon !== false) {
      return searchIcon;
    }
    return (
      <FlexRow
        className="ds-field-select__search-icon"
        align="center"
        justify="center"
        inline
        fullWidth={false}
        aria-hidden
      >
        <SearchIcon size={14} />
      </FlexRow>
    );
  }, [showSearchIcon, searchIcon]);

  const triggerLabel = useMemo(() => {
    if (!selectedOption) return placeholder;
    if (renderSelectedLabel) return renderSelectedLabel(selectedOption);
    return selectedOption.label;
  }, [selectedOption, renderSelectedLabel, placeholder]);

  const rootClass = [
    'ds-field-select',
    `ds-field-select--${variant}`,
    size === 'sm' && 'ds-field-select--size-sm',
    block && 'ds-field-select--block',
    isOpen ? 'ds-field-select--open' : '',
    disabled && 'ds-field-select--disabled',
    error && 'ds-field-select--error',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <SelectCloseContext.Provider value={closeSelect} style={{ paddingTop: '0px', paddingBottom: '0px' }}>
      <FlexColumn
        ref={rootRef}
        className={rootClass.trim()}
        style={rootVars}
        align="start"
        inline={!block}
        fullWidth={Boolean(block)}
      >
        <FlexColumn
          className="ds-field-select__trigger-wrap"
          onClick={toggleOpen}
          onKeyDown={handleTriggerKeyDown}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={ariaLabel}
          aria-disabled={disabled || undefined}
          align="stretch"
          fullWidth
        >
          <FlexRow
            id={controlId}
            className={[
              'ds-field-select__field',
              !showChevron && 'ds-field-select__field--no-chevron',
              isOpen && 'ds-field-select__field--open',
              disabled && 'ds-field-select__field--disabled',
            ]
              .filter(Boolean)
              .join(' ')}
            justify="between"
            align="center"
            aria-invalid={error || undefined}
          >
            <Typography
              variant="body1"
              component="span"
              className={selectedOption ? 'ds-field-select__value' : 'ds-field-select__placeholder'}
            >
              {triggerLabel}
            </Typography>
            {showChevron && (
              <FlexRow
                className={`ds-field-select__chevron ${isOpen ? 'ds-field-select__chevron--open' : ''}`}
                align="center"
                justify="center"
                inline
                fullWidth={false}
                aria-hidden
              >
                <ChevronDownIcon />
              </FlexRow>
            )}
          </FlexRow>
        </FlexColumn>

        {isOpen && (
          <FlexColumn
            className={`ds-field-select__menu ds-field-select__menu--${align} ${menuClassName}`.trim()}
            role="menu"
            align="start"
            fullWidth
          >
            {searchable && (
              <Typography
                component="label"
                variant="body1"
                className={['ds-field-select__search', !showSearchIcon && 'ds-field-select__search--no-icon']
                  .filter(Boolean)
                  .join(' ')}
                htmlFor={searchInputId}
              >
                {showSearchIcon && searchIconNode}
                <Input
                  id={searchInputId}
                  type="search"
                  className="ds-field-select__search-input"
                  placeholder={searchPlaceholder}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  aria-label={searchPlaceholder}
                />
              </Typography>
            )}
            <FlexColumn className="ds-field-select__list" role="presentation" align="start" fullWidth>
              {filteredOptions.map((option) => (
                <SelectOptionRow
                  key={String(option.value)}
                  option={option}
                  selected={String(option.value) === String(selectedValue)}
                  onPick={setValue}
                />
              ))}
            </FlexColumn>
          </FlexColumn>
        )}
      </FlexColumn>
    </SelectCloseContext.Provider>
  );
};

Select.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node.isRequired,
      subtitle: PropTypes.node,
      avatar: PropTypes.node,
    })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  searchable: PropTypes.bool,
  disabled: PropTypes.bool,
  align: PropTypes.oneOf(['left', 'right', 'center']),
  className: PropTypes.string,
  style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  /** Full bordered field (default) or borderless compact trigger for InputGroup affixes */
  variant: PropTypes.oneOf(['field', 'affix']),
  /** Omit for default sizing; `sm` tightens trigger padding and height */
  size: PropTypes.oneOf(['sm', 'md']),
  /** When true, root stretches to 100% width (form fields) */
  block: PropTypes.bool,
  showChevron: PropTypes.bool,
  /** Custom search icon node, or `false` to hide icon */
  searchIcon: PropTypes.node,
  id: PropTypes.string,
  menuClassName: PropTypes.string,
  renderSelectedLabel: PropTypes.func,
  /** CSS length values applied as custom properties on the root */
  triggerMinHeight: PropTypes.string,
  triggerWidth: PropTypes.string,
  menuMinWidth: PropTypes.string,
  menuMaxWidth: PropTypes.string,
  listMaxHeight: PropTypes.string,
  ariaLabel: PropTypes.string,
  /** When true, trigger uses the same error border/background treatment as `Input`. */
  error: PropTypes.bool,
};

export default Select;
export { SelectCloseContext };
